import { canSelfRenew } from './renew.ts';
import { scopesAllow, VISIOUS_SCOPES, type AuthScope } from './scopes.ts';
import { authTokenExpired, type AuthTokenClaims } from './token.ts';

/**
 * What a stored grant *means* — the reasoning behind "are you signed in?", as a pure function.
 *
 * This proved the grant works and left the question of state to its check table. A real
 * app has to answer it on every page: the shell draws a padlock or does not, three destinations are
 * gated or are not, and the sign-in page has to say which of four unhappy things is true rather than
 * "signed out" for all of them. That decision is identical on Android — the credential store differs,
 * the reading of it does not — so it is here rather than in `platform/session.ts` (R13, R9).
 *
 * **Every arm below is decidable client-side, and deciding it is the point.** The instance answers
 * all five auth failures with the same `403` (`auth/failure.ts`), so a client that waits for a
 * request to fail cannot distinguish "your token aged out" from "this grant was never wide enough" —
 * and re-running the consent flow on the second one loops forever, which is the trap
 * `decideAuthRecovery` (in `failure.ts`) exists for. The claims carry the expiry and the scopes, so
 * both are knowable *before* a call is made.
 */

export type SessionStatus =
  /** No token for this instance. The ordinary state, not a failure. */
  | 'signed-out'
  /** A token whose `expire` claim is past. Re-running the same grant fixes it. */
  | 'expired'
  /**
   * A token is stored and its claims could not be read at all — not JSON, or JSON without the two
   * fields that make it a credential. Distinct from `signed-out` because there is something to
   * *delete*: telling somebody to sign in again while a broken value sits in storage is advice that
   * cannot work.
   *
   * §8's double-encoding trap does **not** land here on web, and it is worth saying so: the token store
   * refuses a `%7B`-prefixed value at the boundary (`looksPercentEncoded`), so that case reads as
   * `signed-out`. This arm is for a value that got past *that* check and still is not a credential.
   */
  | 'unreadable'
  /**
   * Usable, but the grant does not cover everything this build asks for — so some pages will fail
   * with `Invalid scope` no matter how many times the token is refreshed. Reachable without any
   * mistake: a token minted by an earlier version of Visious outlives the release that widened
   * {@link VISIOUS_SCOPES}.
   */
  | 'under-scoped'
  | 'active';

export interface SessionFacts {
  readonly status: SessionStatus;
  /**
   * Whether an authenticated request can be expected to work. `under-scoped` counts — the reader is
   * signed in and most of the app works — while `expired` and `unreadable` do not, because a page
   * that renders as though it were signed in will only get a 403 for its trouble.
   */
  readonly signedIn: boolean;
  /** From the callback, not from the token: the API has no "who am I" endpoint (`AuthGrant`). */
  readonly username: string | null;
  readonly expireEpochSec: number | null;
  /** Negative once past. `null` when the instance minted the token without an `expire` claim. */
  readonly secondsUntilExpiry: number | null;
  /**
   * Close enough to expiry to say so while the session still works. A token that dies unannounced
   * takes a subscription feed with it and looks like the instance broke.
   */
  readonly expiringSoon: boolean;
  /** The grant as the instance wrote it, verbatim — including scope strings core cannot parse. */
  readonly grantedScopes: readonly string[];
  /** Empty unless `status` is `under-scoped`; the scopes a re-grant has to ask for. */
  readonly missingScopes: readonly AuthScope[];
  /**
   * Holds the opt-in renewal scope (`auth/renew.ts`), so it will renew itself rather than expire.
   * What `expiringSoon` means depends on it: with this true the app acts, without it the app asks.
   */
  readonly canSelfRenew: boolean;
}

/*
 * There is deliberately no precomputed `action: AuthRecovery` on these facts, and it was written and
 * removed rather than never considered. It read well — one answer to "what now", from the same taxonomy
 * the 403 classifier uses — and **nothing consumed it**: the sign-in page's four button labels are
 * keyed to the *status* (`signed-out` and `unreadable` share an action and need different words), and a
 * page recovering from an actual 403 has the failure kind in hand and calls `decideAuthRecovery`
 * (`failure.ts`) directly. A field nobody reads is a claim nobody checks, so the mapping lives at its one real call
 * site instead.
 */

/** Three days. Long enough to notice on a weekly-use app, short enough not to nag for a month. */
export const SESSION_EXPIRY_WARNING_SEC = 3 * 24 * 60 * 60;

export interface SessionInput {
  /** Something is stored. Kept separate from `claims` so "stored but unreadable" has a name. */
  readonly hasToken: boolean;
  /** `null` either because nothing is stored or because the stored value did not parse. */
  readonly claims: AuthTokenClaims | null;
  readonly username: string | null;
  readonly nowEpochSec: number;
  /** Defaults to {@link VISIOUS_SCOPES}; injectable so a test can widen the ask without editing R9. */
  readonly requiredScopes?: readonly AuthScope[];
}

export function describeSession(input: SessionInput): SessionFacts {
  const empty = {
    username: null,
    expireEpochSec: null,
    secondsUntilExpiry: null,
    expiringSoon: false,
    grantedScopes: [] as readonly string[],
    missingScopes: [] as readonly AuthScope[],
    canSelfRenew: false,
  };

  if (!input.hasToken) return { ...empty, status: 'signed-out', signedIn: false };
  // There is a credential to *delete* here, which is what separates it from `signed-out`.
  if (input.claims === null) {
    return { ...empty, status: 'unreadable', signedIn: false, username: input.username };
  }

  const claims = input.claims;
  const expiry = claims.expireEpochSec;
  const secondsUntilExpiry = expiry === null ? null : expiry - input.nowEpochSec;
  const base = {
    username: input.username,
    expireEpochSec: expiry,
    secondsUntilExpiry,
    grantedScopes: claims.rawScopes,
    canSelfRenew: canSelfRenew(claims.scopes),
  };

  if (authTokenExpired(claims, input.nowEpochSec)) {
    return {
      ...base,
      status: 'expired',
      signedIn: false,
      expiringSoon: false,
      missingScopes: [],
    };
  }

  // A token with no `expire` claim never warns: "expires in ? days" is not a warning, and the
  // instance we ship against always sends one.
  const expiringSoon = secondsUntilExpiry !== null && secondsUntilExpiry <= SESSION_EXPIRY_WARNING_SEC;
  const missingScopes = missingScopeGrants(claims.scopes, input.requiredScopes ?? VISIOUS_SCOPES);

  if (missingScopes.length > 0) {
    return {
      ...base,
      status: 'under-scoped',
      signedIn: true,
      expiringSoon,
      missingScopes,
    };
  }

  return {
    ...base,
    status: 'active',
    signedIn: true,
    expiringSoon,
    // `expiringSoon` is a *fact*, not an instruction: re-authorizing early is offered on the page as
    // something the reader may do, never as something that must happen before they can read a feed.
    missingScopes: [],
  };
}

/**
 * Which of the scopes this build needs the granted set does not cover.
 *
 * Semantic rather than textual: comparing `formatAuthScope` strings would report
 * `GET;POST;DELETE:subscriptions*` as missing when the instance granted the equivalent
 * `GET;POST;DELETE:subscriptions*` with its methods in another order, and would call a *wider* grant
 * insufficient. So each required scope is probed through {@link scopesAllow}, the same predicate the
 * API layer uses to refuse a call before making it.
 *
 * The wildcard needs its own probe, and this is the non-obvious line: `subscriptions*` is not covered
 * by a grant of plain `subscriptions`, yet both answer the same for the endpoint itself. Probing
 * `subscriptions/` — a path *below* it — is what separates them, because that is exactly what the
 * trailing `*` buys (`endpointMatches`).
 */
export function missingScopeGrants(
  granted: readonly AuthScope[],
  required: readonly AuthScope[],
): readonly AuthScope[] {
  return required.filter((scope) => !grantCovers(granted, scope));
}

/**
 * There is deliberately no "widen the request" function to go with `'widen-scopes'`.
 *
 * The obvious one — ask for the union of what is held and what is missing — would mint a token
 * carrying permissions this build does not use, which is exactly what R9's enumerated list exists to
 * prevent. And it buys nothing: {@link VISIOUS_SCOPES} *is* this build's complete ask, so re-running
 * the grant unchanged already asks for every missing scope. `'widen-scopes'` differs from
 * `'reauthorize'` in what the reader is told, not in what is requested — the trap
 * `decideAuthRecovery` guards against is re-running the grant on a failure a re-grant cannot
 * fix, and a scope gap is not one of those.
 */
function grantCovers(granted: readonly AuthScope[], required: AuthScope): boolean {
  // An empty method list means "any method" (the instance writes its own CSRF scopes that way), and
  // nothing can prove *that* is covered by probing a finite list — so it is only satisfied by a
  // grant that is itself method-less for the same endpoint.
  const methods = required.methods.length > 0 ? required.methods : null;
  const endpoints = required.wildcard ? [required.endpoint, `${required.endpoint}/`] : [required.endpoint];

  if (methods === null) {
    return granted.some(
      (scope) =>
        scope.methods.length === 0 && endpoints.every((endpoint) => scopesAllow([scope], 'GET', endpoint)),
    );
  }

  return methods.every((method) => endpoints.every((endpoint) => scopesAllow(granted, method, endpoint)));
}
